import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { tap, catchError, of, Observable } from 'rxjs';
import { ExceptionManager } from 'src/app/components/core/_infrastructure/classes/handlers/exceptionManager';
import { AuthService } from 'src/app/components/core/_infrastructure/classes/services/auth.service';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { NavMenuService } from 'src/app/components/core/components/navmenu/navmenu.service';
import { DocumentoUICamViewModel, TwUnidadInspeccionViewModelExtended } from '../../unidad-inspeccion-datosbasicos.types';
import { ComponentBase } from 'src/app/components/core/_infrastructure/classes/componentBase';
import { UnidadInspeccionService } from '../../unidad-inspeccion.service';
import { EnumCodigosTwTcAcciones } from 'src/app/components/_importacion/solicitudes/types/enum';
import { ConfirmationService } from 'primeng/api';
import { DocumentoUiService } from '../../documento-ui.service';

@Component({
    selector: 'app-documento-ui-wrapper-edit',
    templateUrl: './documento-ui-wrapper-edit.component.html',
    styleUrls: ['./documento-ui-wrapper-edit.component.scss'],
})
export class DocumentoUiWrapperEditComponent extends ComponentBase implements OnInit, OnDestroy {
    @HostListener('window:beforeunload', ['$event'])
    forceCheckUnload(): boolean {
        // Prevenimos la pérdida de datos, si el usuario recarga accidentalmente la página.
        // Lo controlamos y el navegador le requiere confirmación para finalizar la recarga.
        this.purgeNotAttachedFiles();
        return false;
    }
    /**
     * @article: Reactive forms with smart dumb components
     * https://medium.com/angular-athens/reactive-forms-with-smart-dumb-components-48ebc81417c7
     */

    unidadInspeccion: TwUnidadInspeccionViewModelExtended;
    private documentosUIListUrl = '/campanias/unidadinspeccion/documentos/';

    title = { plural: 'Documentos', singular: 'Documento' };
    visibilitySpinnerGuardando = false;
    documentoUploadedUI: DocumentoUICamViewModel | null = null;
    documentoDetalleUI: DocumentoUICamViewModel | null = null;
    formGroup: FormGroup;
    private filePendingToPurge = '';
    successMessage = 'Documentación guardada correctamente';
    loseChangesQuestion = '¿Desea perder los cambios pendientes?';

    private activatedRoute$ = this.activatedRoute.paramMap.subscribe(params => {
        // A éste componente se llega de dos formas:
        // - por el "create", que solo recibirá el idUI
        // - por el "detail", que recibirá idUI y id (de documento)

        const idUi: any = params.get('idUi');
        if (idUi) {
            this.getUnidadInspeccion(idUi);
        }
        const id: any = params.get('id');
        if (id) {
            this.getDocumento(id);
        }
    });

    constructor(
        private fb: FormBuilder,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private unidadInspeccionService: UnidadInspeccionService,
        private docUiCamService: DocumentoUiService,
        private confirmationService: ConfirmationService,
        protected notificationService: NotificationService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
        private navMenuService: NavMenuService,
    ) {
        super(authService, notificationService, exceptionHandler);
    }

    ngOnInit(): void {
        this.preparePresentation();
    }

    private preparePresentation(): void {
        this.buildForm();
    }

    private buildForm(): void {
        this.formGroup = this.fb.group({
            Descripcion: ['', Validators.required],
            NombreArchivo: ['', Validators.required],
        });
        this.form = this.formGroup;
    }

    /**
     * Se cargan los datos de la unidad de inspección y sus recintos "PI-A-Ed" asociados
     * Una vez obtenida, establece el valor para la propiedad readonly a partir de la información del usuario y los permisos de edición.
     * Después invoca al servicio de obtención de inspecciones de la U.I.
     * @param id number
     */
    private getUnidadInspeccion(id: number): void {
        this.unidadInspeccionService
            .getUI(id)
            .pipe(
                tap((udInspeccion: TwUnidadInspeccionViewModelExtended) => {
                    if (!udInspeccion?.FirstRecinto) {
                        console.error('UI sin recintos/cultivos');
                        return;
                    }

                    this.unidadInspeccion = udInspeccion;
                    this.navMenuService.updateMenuServiceForUdInspeccion(
                        udInspeccion.IdUi!,
                        udInspeccion.IdEstadoUi!,
                        udInspeccion.FirstRecinto?.IdTipoSolicitudCam || 0,
                        udInspeccion.PeriodoCertificaKgs,
                    );
                }),
                catchError(error => {
                    this.catchServerError(error);
                    this.formGroup.disable();
                    return of(null);
                }),
            )
            .subscribe();
    }

    /**
     * @param id number
     */
    private getDocumento(id: number): void {
        this.docUiCamService
            .getDocumentoById(id)
            .pipe(
                tap((doc: DocumentoUICamViewModel) => {
                    this.documentoDetalleUI = doc;
                }),
                catchError(error => {
                    this.catchServerError(error);
                    this.formGroup.disable();
                    return of(null);
                }),
            )
            .subscribe();
    }

    /**
     * Se recibe el documento cargado en la ventana de detalle
     * Guardamos el guid (codigo-storage) del fichero que se ha subido, y se purga el posible fichero anterior que no se haya guardado.
     * Puede ocurrir que se cierre la ventana sin haber guardado la documentación.
     * Si detectamos ése caso, se purga el fichero, esto es, se elimina de la BD.
     */
    onFileUpload(documento: DocumentoUICamViewModel): void {
        this.documentoUploadedUI = documento;
        if (this.filePendingToPurge !== '') {
            this.purgeNotAttachedFiles();
        }
        this.filePendingToPurge = documento.CodigoStorage;
    }

    /**
     * Invocación de la API para salvar los cambios realizados en el PV
     * @return void
     */
    onClickSave(): void {
        // Comprobar si se ha cargado el documento o si hay ficheros subidos
        if (this.filePendingToPurge === '' || this.documentoUploadedUI === null) {
            this.addErrorMessage('No se ha cargado el documento');
            return;
        }

        if (!this.form.valid) {
            this.addErrorMessage('Revise la documentación');
            return;
        }

        this.filePendingToPurge = '';
        this.visibilitySpinnerGuardando = true;

        //se guarda el documento
        const modelForm = this.formGroup.getRawValue();
        let model = new DocumentoUICamViewModel();
        model.Id = 0;
        model.Descripcion = modelForm.Descripcion;
        model.NombreArchivo = this.documentoUploadedUI.NombreArchivo;
        model.IdUnidadInspeccion = this.unidadInspeccion.IdUi!;
        model.CodigoStorage = this.documentoUploadedUI.CodigoStorage;
        model.FileSize = this.documentoUploadedUI.FileSize;

        this.docUiCamService
            .save(model)
            .pipe(
                tap(() => {
                    this.visibilitySpinnerGuardando = false;
                    this.addSuccessMessage(this.successMessage);
                    this.onBack();
                }),
                catchError(error => {
                    this.visibilitySpinnerGuardando = false;
                    this.catchServerError(error);
                    return of(null);
                }),
            )
            .subscribe();
    }

    onBack(): void {
        this.router.navigate([this.documentosUIListUrl + this.unidadInspeccion.IdUi]);
    }

    get hasAddingAccess(): boolean {
        if (!this.unidadInspeccion) return false;

        const hasAddingAccess = this.loggedInUser!.getAction(
            EnumCodigosTwTcAcciones.ADD_DOC_UI,
            this.unidadInspeccion.IdEstadoUi,
            this.unidadInspeccion.FirstRecinto.IdTipoSolicitudCam,
        ).Editable;

        return hasAddingAccess && this.unidadInspeccion?.FirstRecinto?.PeriodoEnPlazoVigente;
    }

    get readonly(): boolean {
        return this.documentoDetalleUI ? this.documentoDetalleUI?.Id !== null : false;
    }

    get actionNotAllowed(): boolean {
        return this.visibilitySpinnerGuardando || this.formInvalid || !this.unidadInspeccion?.FirstRecinto?.PeriodoEnPlazoVigente;
    }

    private get formInvalid(): boolean {
        return !this.formGroup.valid || !this.formGroup.dirty || this.formGroup.pristine || this.filePendingToPurge === '';
    }

    /***********************************************************************************************************************************************/
    /* Métodos para controlar que si se cierra accidentalmente la ventana y se han quedado ficheros sin vincular a la documentación, sean borrados */
    /***********************************************************************************************************************************************/

    /**
     * Si el usuario ha subido ficheros, pero no se han guardado en la solicitud, los purgamos de la BD.
     * Sabemos que aquellos "adjuntos" que no tienen Id, no existen en la BD, así que se descartan directamente.
     */
    private purgeNotAttachedFiles(): void {
        if (this.filePendingToPurge === '' || this.readonly) {
            return;
        }
        this.docUiCamService.deleteFile(this.filePendingToPurge).subscribe();
    }

    // Usado en unidad-inspeccion-routing.module.ts
    async canDeactivate(): Promise<boolean | Observable<boolean>> {
        setTimeout(() => {
            //
        }, 100);
        if (this.filePendingToPurge !== '') {
            const response = await this.toLoseChangesQuestion();
            return response;
        } else {
            return true;
        }
    }

    private async toLoseChangesQuestion(): Promise<boolean> {
        return new Promise(resolve => {
            this.confirmationService.confirm({
                message: this.loseChangesQuestion,
                accept: () => {
                    this.purgeNotAttachedFiles();
                    resolve(true);
                },
                reject: () => {
                    resolve(false);
                },
            });
        });
    }

    ngOnDestroy(): void {
        this.purgeNotAttachedFiles();
    }
}
